/**
 * Entry API Client
 *
 * Provides methods for interacting with the Entry Manager API.
 */

import { fetchApi, buildQueryString } from './client';
import type {
  EntryFilters,
  EntryListResponse,
  EntryDetailResponse,
  UpdateEntryInput,
  EntryStats,
  EntryAssociations,
  EntryTypeOption,
} from '@/types/entry';

// API endpoint for entries (without /api prefix - client.ts adds it)
const ENTRIES_BASE = '/entries';

/**
 * List entries with filtering and pagination.
 */
export async function listEntries(
  filters: EntryFilters = {},
  pagination?: { limit?: number; offset?: number }
): Promise<EntryListResponse> {
  const params: Record<string, string | number | boolean | undefined> = {
    entry_type: filters.entryType,
    source_system: filters.sourceSystem,
    recorded_after: filters.recordedAfter,
    recorded_before: filters.recordedBefore,
    search: filters.search,
    has_overrides: filters.hasOverrides,
    processing_status: filters.processingStatus,
    sort_by: filters.sortBy,
    sort_order: filters.sortOrder,
    limit: pagination?.limit,
    offset: pagination?.offset,
  };

  const query = buildQueryString(params);
  return fetchApi<EntryListResponse>(`${ENTRIES_BASE}${query}`);
}

/**
 * Get a single entry by ID.
 */
export async function getEntry(
  entryId: string,
  includeAssociations = false
): Promise<EntryDetailResponse> {
  const query = includeAssociations ? '?include_associations=true' : '';
  return fetchApi<EntryDetailResponse>(`${ENTRIES_BASE}/${entryId}${query}`);
}

/**
 * Update entry user overrides.
 */
export async function updateEntry(
  entryId: string,
  input: UpdateEntryInput
): Promise<EntryDetailResponse> {
  return fetchApi<EntryDetailResponse>(`${ENTRIES_BASE}/${entryId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

/**
 * Get associations for an entry (linked tasks).
 */
export async function getEntryAssociations(
  entryId: string
): Promise<EntryAssociations> {
  return fetchApi<EntryAssociations>(`${ENTRIES_BASE}/${entryId}/associations`);
}

/**
 * Get entry statistics.
 */
export async function getEntryStats(): Promise<EntryStats> {
  return fetchApi<EntryStats>(`${ENTRIES_BASE}/stats`);
}

/**
 * Get available entry types.
 */
export async function getEntryTypes(): Promise<{ types: EntryTypeOption[] }> {
  return fetchApi<{ types: EntryTypeOption[] }>(`${ENTRIES_BASE}/types`);
}

/**
 * Get available source systems.
 */
export async function getSourceSystems(): Promise<{ sources: string[] }> {
  return fetchApi<{ sources: string[] }>(`${ENTRIES_BASE}/sources`);
}
